import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Calculator, 
  TrendingUp, 
  Users, 
  Percent,
  Euro,
  Calendar,
  ChevronRight,
  Lock,
  Unlock,
  Info,
  Building2,
  Briefcase,
  HardHat,
  Utensils,
  Truck,
  FlaskConical,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config';

// Icone per settori
const SETTORE_ICONS = {
  'Terziario': Briefcase,
  'Industria': Building2,
  'Costruzioni': HardHat,
  'Ristorazione': Utensils,
  'Logistica': Truck,
  'Chimica': FlaskConical,
};

// Dati statici per le tabelle informative
const ALIQUOTE_IRPEF_2025 = [
  { scaglione: 'Fino a €28.000', aliquota: '23%', imposta: 'Fino a €6.440' },
  { scaglione: 'Da €28.001 a €50.000', aliquota: '35%', imposta: '€6.440 + 35% eccedenza' },
  { scaglione: 'Oltre €50.000', aliquota: '43%', imposta: '€14.140 + 43% eccedenza' },
];

const ALIQUOTE_INPS_2025 = [
  { categoria: 'Lavoratori dipendenti', aliquota_dipendente: '9,19%', aliquota_azienda: '23,81%', totale: '33%' },
  { categoria: 'Apprendisti', aliquota_dipendente: '5,84%', aliquota_azienda: '11,61%', totale: '17,45%' },
  { categoria: 'Dirigenti industria', aliquota_dipendente: '9,19%', aliquota_azienda: '23,81%', totale: '33%' },
];

const COEFFICIENTI_TFR_2025 = [
  { anno: '2025', coefficiente: '1,015 + 75% inflazione' },
  { anno: '2024', coefficiente: '2,828%' },
  { anno: '2023', coefficiente: '4,678%' },
  { anno: '2022', coefficiente: '9,974%' },
  { anno: '2021', coefficiente: '3,245%' },
];

const TASSO_INTERESSE_LEGALE = [
  { anno: '2025', tasso: '2,00%' },
  { anno: '2024', tasso: '2,50%' },
  { anno: '2023', tasso: '5,00%' },
  { anno: '2022', tasso: '1,25%' },
  { anno: '2021', tasso: '0,01%' },
];

export default function BancaDatiPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('tabelle_ccnl');
  const [ccnlList, setCcnlList] = useState([]);
  const [selectedCcnl, setSelectedCcnl] = useState(null);
  const [ccnlDetails, setCcnlDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [freeViewsToday, setFreeViewsToday] = useState(0);
  const [userCredits, setUserCredits] = useState(0);
  const [remainingFree, setRemainingFree] = useState(3);

  const MAX_FREE_VIEWS = 3;

  useEffect(() => {
    fetchCcnlList();
    fetchFreeViewsStatus();
  }, []);

  const fetchFreeViewsStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API_URL}/bancadati/free-views`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFreeViewsToday(response.data.views_today || 0);
        setRemainingFree(response.data.remaining_free || 0);
        setUserCredits(response.data.credits || 0);
      }
    } catch (err) {
      console.error('Error fetching free views status:', err);
      // Fallback per utenti non loggati
      setRemainingFree(MAX_FREE_VIEWS);
    }
  };

  const fetchCcnlList = async () => {
    try {
      const response = await axios.get(`${API_URL}/ccnl/list`);
      setCcnlList(response.data.ccnl || []);
    } catch (err) {
      console.error('Error fetching CCNL list:', err);
    }
  };

  const viewCcnlDetails = async (ccnlId) => {
    const token = localStorage.getItem('token');
    
    // Se non loggato, permetti comunque di vedere (gestione server-side opzionale)
    if (!token) {
      toast.error('Effettua il login per accedere alla Banca Dati');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      // Prima registra la visualizzazione (free o a crediti)
      const useViewResponse = await axios.post(`${API_URL}/bancadati/use-view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Aggiorna stato locale
      if (useViewResponse.data.used_free) {
        setFreeViewsToday(useViewResponse.data.views_today);
        setRemainingFree(useViewResponse.data.remaining_free);
        toast.success(`Visualizzazione gratuita (${useViewResponse.data.views_today}/${MAX_FREE_VIEWS} oggi)`);
      } else {
        setUserCredits(useViewResponse.data.credits_remaining);
        toast.info(`Utilizzato 1 credito. Rimanenti: ${useViewResponse.data.credits_remaining}`);
      }
      
      // Poi carica i dettagli CCNL
      const response = await axios.get(`${API_URL}/ccnl/${ccnlId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCcnlDetails(response.data);
      setSelectedCcnl(ccnlId);
      
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Hai esaurito le visualizzazioni gratuite giornaliere. Acquista crediti per continuare.');
        navigate('/credits');
      } else {
        toast.error('Errore nel caricamento dei dettagli CCNL');
      }
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { id: 'tabelle_ccnl', label: 'Tabelle retributive CCNL', icon: FileText },
    { id: 'aliquote_irpef', label: 'Aliquote IRPEF', icon: Percent },
    { id: 'aliquote_inps', label: 'Aliquote contributive', icon: Users },
    { id: 'coefficiente_tfr', label: 'Coefficiente rivalut. TFR', icon: TrendingUp },
    { id: 'tasso_interesse', label: 'Tasso interesse legale', icon: Euro },
    { id: 'calcola_netto', label: 'Calcola netto in busta', icon: Calculator },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'tabelle_ccnl':
        return renderTabelleCCNL();
      case 'aliquote_irpef':
        return renderAliquoteIRPEF();
      case 'aliquote_inps':
        return renderAliquoteINPS();
      case 'coefficiente_tfr':
        return renderCoefficienteTFR();
      case 'tasso_interesse':
        return renderTassoInteresse();
      case 'calcola_netto':
        return renderCalcolaNetto();
      default:
        return renderTabelleCCNL();
    }
  };

  const renderTabelleCCNL = () => (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Gift className="w-6 h-6 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900">Consultazioni Gratuite</h3>
            <p className="text-sm text-blue-700">
              Hai <strong>{remainingFree}</strong> visualizzazioni gratuite rimanenti oggi.
              Dopo le {MAX_FREE_VIEWS} gratuite, ogni consultazione costa 1 credito.
            </p>
          </div>
        </div>
      </div>

      {/* CCNL Details Modal */}
      {selectedCcnl && ccnlDetails && (
        <Card className="border-2 border-forest">
          <CardHeader className="bg-forest text-white">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">{ccnlDetails.nome}</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-white/20"
                onClick={() => { setSelectedCcnl(null); setCcnlDetails(null); }}
              >
                ✕
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-50 p-3 rounded">
                <span className="text-sm text-gray-500">Settore</span>
                <p className="font-semibold">{ccnlDetails.settore}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <span className="text-sm text-gray-500">Mensilità</span>
                <p className="font-semibold">{ccnlDetails.mensilita}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <span className="text-sm text-gray-500">Ore Settimanali</span>
                <p className="font-semibold">{ccnlDetails.ore_settimanali}</p>
              </div>
            </div>

            <h4 className="font-semibold mb-2 text-forest">Tabella Retributiva</h4>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-forest/10">
                    <TableHead>Livello</TableHead>
                    <TableHead>Denominazione</TableHead>
                    <TableHead className="text-right">Paga Base</TableHead>
                    <TableHead className="text-right">Contingenza</TableHead>
                    <TableHead className="text-right">Totale Minimo</TableHead>
                    <TableHead className="text-right">Scatto Anz.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ccnlDetails.livelli && Object.entries(ccnlDetails.livelli).map(([key, liv]) => (
                    <TableRow key={key} className="hover:bg-gray-50">
                      <TableCell className="font-semibold">{key}</TableCell>
                      <TableCell>{liv.nome}</TableCell>
                      <TableCell className="text-right font-mono">€{liv.paga_base?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono">€{liv.contingenza?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-forest">€{liv.totale_minimo?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono">€{liv.scatto_anzianita?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {ccnlDetails.maggiorazioni && (
              <>
                <h4 className="font-semibold mb-2 mt-6 text-forest">Maggiorazioni Straordinario</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(ccnlDetails.maggiorazioni).map(([key, value]) => (
                    <div key={key} className="bg-orange-50 border border-orange-200 p-3 rounded text-center">
                      <span className="text-xs text-gray-600 block">{key.replace(/_/g, ' ')}</span>
                      <span className="text-lg font-bold text-orange-600">
                        {typeof value === 'number' && value < 100 ? `${value}%` : `€${value}`}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* CCNL List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ccnlList.map((ccnl) => {
          const IconComponent = SETTORE_ICONS[ccnl.settore] || Building2;
          return (
            <Card 
              key={ccnl.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-forest"
              onClick={() => viewCcnlDetails(ccnl.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-forest/10 rounded-lg flex items-center justify-center">
                    <IconComponent className="w-5 h-5 text-forest" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{ccnl.nome}</h3>
                    <p className="text-sm text-gray-500">{ccnl.settore}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">
                        {ccnl.livelli?.length || Object.keys(ccnl.livelli || {}).length} livelli
                      </Badge>
                      {freeViewsToday < MAX_FREE_VIEWS ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <Unlock className="w-3 h-3 mr-1" />
                          Gratis
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-700 text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          1 credito
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const renderAliquoteIRPEF = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-forest" />
          Aliquote IRPEF 2025
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Scaglione di Reddito</TableHead>
              <TableHead className="text-center">Aliquota</TableHead>
              <TableHead>Imposta Dovuta</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ALIQUOTE_IRPEF_2025.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium">{row.scaglione}</TableCell>
                <TableCell className="text-center">
                  <Badge className="bg-forest">{row.aliquota}</Badge>
                </TableCell>
                <TableCell>{row.imposta}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Detrazioni per Lavoro Dipendente 2025</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Reddito fino a €15.000: detrazione €1.955</li>
            <li>• Reddito €15.001 - €28.000: detrazione decrescente</li>
            <li>• Reddito €28.001 - €50.000: detrazione €1.910 × (50.000 - reddito) / 22.000</li>
            <li>• Reddito oltre €50.000: nessuna detrazione</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );

  const renderAliquoteINPS = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-forest" />
          Aliquote Contributive INPS 2025
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Categoria</TableHead>
              <TableHead className="text-center">Quota Dipendente</TableHead>
              <TableHead className="text-center">Quota Azienda</TableHead>
              <TableHead className="text-center">Totale</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ALIQUOTE_INPS_2025.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium">{row.categoria}</TableCell>
                <TableCell className="text-center">{row.aliquota_dipendente}</TableCell>
                <TableCell className="text-center">{row.aliquota_azienda}</TableCell>
                <TableCell className="text-center">
                  <Badge className="bg-forest">{row.totale}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
          <h4 className="font-semibold text-yellow-900 mb-2">Massimali e Minimali 2025</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-yellow-700">Massimale annuo:</span>
              <span className="font-semibold ml-2">€119.650</span>
            </div>
            <div>
              <span className="text-yellow-700">Minimale giornaliero:</span>
              <span className="font-semibold ml-2">€56,87</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderCoefficienteTFR = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-forest" />
          Coefficiente Rivalutazione TFR
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold mb-2">Formula di Calcolo</h4>
          <p className="text-sm text-gray-700">
            <code className="bg-gray-200 px-2 py-1 rounded">Rivalutazione = 1,5% + (75% × Indice ISTAT FOI)</code>
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Anno</TableHead>
              <TableHead>Coefficiente di Rivalutazione</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {COEFFICIENTI_TFR_2025.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium">{row.anno}</TableCell>
                <TableCell>
                  <Badge variant="outline">{row.coefficiente}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  const renderTassoInteresse = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Euro className="w-5 h-5 text-forest" />
          Tasso di Interesse Legale
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Anno</TableHead>
              <TableHead>Tasso</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {TASSO_INTERESSE_LEGALE.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium">{row.anno}</TableCell>
                <TableCell>
                  <Badge className="bg-forest">{row.tasso}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="mt-4 p-4 bg-green-50 rounded-lg">
          <p className="text-sm text-green-800">
            Il tasso di interesse legale viene utilizzato per il calcolo degli interessi di mora, 
            rivalutazione crediti di lavoro e TFR non versato.
          </p>
        </div>
      </CardContent>
    </Card>
  );

  const renderCalcolaNetto = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-forest" />
          Calcola Netto in Busta
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8">
          <Calculator className="w-16 h-16 text-forest mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Calcolo Automatico</h3>
          <p className="text-gray-600 mb-6">
            Per calcolare il netto in busta, carica la tua busta paga nella sezione "Nuova Busta".
            Il sistema calcolerà automaticamente tutti i valori.
          </p>
          <Button 
            onClick={() => navigate('/upload')}
            className="bg-forest hover:bg-forest/90"
          >
            Carica Busta Paga
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Banca Dati</h1>
            <p className="text-gray-500">Tabelle retributive CCNL e informazioni fiscali</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <span className="text-gray-500">Visualizzazioni gratuite oggi:</span>
              <Badge className="ml-2 bg-green-600">{remainingFree} / {MAX_FREE_VIEWS}</Badge>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Crediti:</span>
              <Badge className="ml-2 bg-forest">{userCredits}</Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Menu */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeSection === item.id
                            ? 'bg-forest text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm font-medium">{item.label}</span>
                        <ChevronRight className={`w-4 h-4 ml-auto ${activeSection === item.id ? 'text-white' : 'text-gray-400'}`} />
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {renderContent()}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
